/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { css as compiledCss } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider } from 'react-intl';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import { useProvider } from '@atlaskit/editor-common/provider-factory';
import Heading from '@atlaskit/heading/heading';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import ProjectIcon from '@atlaskit/icon/core/project';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import UndoIcon from '@atlaskit/icon/core/undo';
import IconTile from '@atlaskit/icon/icon-tile';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Mention from '@atlaskit/mention/mention';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import type ProfileClient from '@atlaskit/profilecard/profile-card-client';
import ProfileCardTrigger from '@atlaskit/profilecard/profile-card-trigger';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/utils/teams-app-navigation/navigate-to-teams-app';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import type {
	HoverEntityCardClickTarget,
	EntityAnnotationOpenMethod,
	InferredProjectFeedbackValue,
	InferredProjectPromotionOutcome,
	InferredProjectPromotionFailure,
} from '@atlassian/conversation-assistant-instrumentation/controllers/analytics/entity-annotation-events';
import { isInferredProjectEntityAnnotationEnabled } from '@atlassian/conversation-assistant-utils/isEntityAnnotationExperimentEnabled';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import {
	type ProfileTriggerRenderer,
	useProfileTrigger,
} from '../../controllers/profile-trigger/ProfileTriggerContext';
import messages from './inferredProjectCardMessages';
import type { InferredProjectPerson, InferredProjectSource } from './parseInferredProjectContext';
import { PromoteInferredProjectSection } from './PromoteInferredProjectSection';
import { RelatedContentSection } from './RelatedContentSection';
import type { RelatedContentLink } from './RelatedContentSection';
import { useInferredProjectContext } from './useInferredProjectContext';
import { usePromoteInferredProject } from './usePromoteInferredProject';

const COMPACT_CONTRIBUTOR_LIMIT = 4;
const HOVER_CLOSE_DELAY_MS = 150;

const projectTitleButtonStyles = compiledCss({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	boxDecorationBreak: 'clone',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'inline',
	font: token('font.body'),
	overflowWrap: 'break-word',
	paddingBlock: '3px',
	paddingInlineEnd: '2px',
	paddingInlineStart: '2px',
	WebkitBoxDecorationBreak: 'clone',
	whiteSpace: 'normal',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width'),
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

const projectIconStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	marginInlineEnd: token('space.025'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const projectTitleStyles = compiledCss({
	minWidth: 0,
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

const projectLabelStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	marginInlineStart: token('space.050'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.medium'),
		maxWidth: '375px',
		minWidth: '320px',
	},
	basedOn: {
		backgroundColor: token('elevation.surface.sunken'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- The design requires a static gradient-and-grid treatment; its colors use design tokens.
		backgroundImage: `linear-gradient(270deg, rgba(248, 248, 248, 0) 0%, ${token('elevation.rovo.surface.overlay')} 100%), linear-gradient(${token('color.border.disabled')} 1px, transparent 1px), linear-gradient(90deg, ${token('color.border.disabled')} 1px, transparent 1px)`,
		backgroundPosition: '0 0, 6px 4px, 6px 4px',
		backgroundSize: '100% 100%, 24px 24px, 24px 24px',
		borderRadius: token('radius.medium'),
	},
	flippedIcon: {
		display: 'inline-flex',
		transform: 'rotate(180deg)',
	},
	sourceLink: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
	},
	contributors: {
		paddingBlockStart: token('space.100'),
	},
	contributorLink: {
		textDecoration: 'none',
	},
	showAllTrigger: {
		backgroundColor: 'transparent',
		paddingInline: '0',
	},
	feedbackFooter: {
		borderBlockStartColor: token('color.border'),
		borderBlockStartStyle: 'solid',
		borderBlockStartWidth: token('border.width'),
		marginBlockStart: token('space.100'),
		paddingBlockStart: token('space.100'),
	},
	feedbackConfirmation: {
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		paddingBlock: token('space.100'),
		paddingInlineEnd: token('space.100'),
		paddingInlineStart: token('space.150'),
	},
});

export type InferredProjectInlineCardProps = {
	canonicalProjectName?: string;
	cloudId: string;
	fallback: React.ReactNode;
	projectId: string;
	projectName: string;
	/** Called when the project hover card opens. */
	onOpen?: (method: EntityAnnotationOpenMethod) => void;
	onPromotionResult?: (
		outcome: InferredProjectPromotionOutcome,
		failure?: InferredProjectPromotionFailure,
	) => void;
	onInteraction?: (targetType: HoverEntityCardClickTarget) => void;
	/** Called when the user gives thumbs feedback on the suggested project. */
	onFeedback?: (feedbackValue: InferredProjectFeedbackValue, inferredProjectId: string) => void;
};

const getAccountId = (ari: string | undefined): string | undefined => {
	const match = ari?.match(/^ari:cloud:identity::user\/(.+)$/);
	return match?.[1];
};

type ContributorChipProps = {
	cloudId: string;
	locale: string;
	person: InferredProjectPerson;
	profileClient: ProfileClient | null;
	renderProfileTrigger?: ProfileTriggerRenderer;
	onClick?: () => void;
};

const ContributorChip = ({
	cloudId,
	locale,
	person,
	profileClient,
	renderProfileTrigger,
	onClick,
}: ContributorChipProps): React.ReactNode => {
	const pill = (
		<IntlProvider locale={locale}>
			<Mention
				avatarUrl={person.avatarUrl}
				id={person.ari ?? person.name}
				renderAvatarSlot={Boolean(person.avatarUrl)}
				text={`@${person.name}`}
			/>
		</IntlProvider>
	);
	const accountId = getAccountId(person.ari);

	if (!accountId) {
		return pill;
	}

	if (isInferredProjectEntityAnnotationEnabled()) {
		const { href } = navigateToTeamsApp({
			type: 'USER',
			payload: { userId: accountId },
			cloudId,
		});

		return (
			<Anchor
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				xcss={styles.contributorLink}
				onClick={onClick}
				onAuxClick={(event) => {
					if (event.button === 1) {
						onClick?.();
					}
				}}
			>
				{pill}
			</Anchor>
		);
	}

	if (renderProfileTrigger) {
		return renderProfileTrigger({ userId: accountId, cloudId, children: pill });
	}

	if (profileClient) {
		return (
			<ProfileCardTrigger
				ariaLabel={person.name}
				cloudId={cloudId}
				resourceClient={profileClient}
				userId={accountId}
			>
				{pill}
			</ProfileCardTrigger>
		);
	}

	return pill;
};

const dedupeContributors = (people: readonly InferredProjectPerson[]): InferredProjectPerson[] => {
	const seen = new Set<string>();
	return people.filter((person) => {
		const key = person.ari ?? person.name;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

// Adapts the promoted project's sources to the shape RelatedContentSection merges/dedupes on. Falls
// back to the url for identity so sources without an id still de-dupe against related content.
const sourcesToRelatedLinks = (sources: readonly InferredProjectSource[]): RelatedContentLink[] =>
	sources.map((source) => ({
		id: source.id ?? source.url,
		url: source.url,
		...(source.title ? { title: source.title } : {}),
	}));

type SourcesLinkListProps = {
	sources: readonly InferredProjectSource[];
	onInteraction?: (targetType: HoverEntityCardClickTarget) => void;
};

// The always-available view of a promoted project's sources. Used on its own as the Suspense/error
// fallback for RelatedContentSection so the source link stays visible while (or if) related content
// fails to load.
const SourcesLinkList = ({ sources, onInteraction }: SourcesLinkListProps): React.ReactNode => {
	if (sources.length === 0) {
		return null;
	}
	return (
		<LinkList
			openLinksInNewTab={isInferredProjectEntityAnnotationEnabled()}
			onLinkClick={(_url, _index, options) => {
				if (!options?.isContextMenu) {
					onInteraction?.('source_document');
				}
			}}
			size="compact"
			titles={sources.map((item) => item.title ?? '')}
			urls={sources.map((item) => item.url)}
		/>
	);
};

type InferredProjectCardContentProps = Omit<InferredProjectInlineCardProps, 'fallback'>;

const InferredProjectCardInner = ({
	canonicalProjectName,
	cloudId,
	projectId,
	projectName,
	onOpen,
	onInteraction,
	onPromotionResult,
	onFeedback,
}: InferredProjectCardContentProps): React.ReactNode => {
	const { formatMessage, locale } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { load, ...state } = useInferredProjectContext({ cloudId, projectId });
	const promotion = usePromoteInferredProject(cloudId, onPromotionResult);
	const hasPromotedProject = promotion.state.status === 'promoted';
	const [isShowingAllContributors, setIsShowingAllContributors] = useState(false);
	const [isSourcesOpen, setIsSourcesOpen] = useState(false);
	const [feedback, setFeedback] = useState<InferredProjectFeedbackValue | null>(null);
	const [isFeedbackConfirmationVisible, setIsFeedbackConfirmationVisible] = useState(false);
	const likeButtonRef = useRef<HTMLButtonElement>(null);
	const dislikeButtonRef = useRef<HTMLButtonElement>(null);
	const profilecardProviderPromise = useProvider('profilecardProvider');
	const renderProfileTrigger = useProfileTrigger();
	const [profileClient, setProfileClient] = useState<ProfileClient | null>(null);

	useEffect(() => {
		let isMounted = true;
		profilecardProviderPromise?.then(({ resourceClient }) => {
			if (isMounted && resourceClient) {
				setProfileClient(resourceClient as ProfileClient);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [profilecardProviderPromise]);

	useEffect(
		() => () => {
			if (closeTimer.current) {
				clearTimeout(closeTimer.current);
			}
		},
		[],
	);

	const open = useCallback(
		(method: EntityAnnotationOpenMethod) => {
			if (!isOpen) {
				load();
				setIsOpen(true);
				onOpen?.(method);
			}
		},
		[isOpen, load, onOpen],
	);
	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		setIsOpen(false);
		// The selected thumb persists across reopen; the confirmation is only shown once.
		setIsFeedbackConfirmationVisible(false);
	}, []);
	const cancelScheduledClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);
	const handleTriggerMouseEnter = useCallback(() => {
		cancelScheduledClose();
		open('hover');
	}, [cancelScheduledClose, open]);
	const handleTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) {
				closeAndUnlock();
			}
		}, HOVER_CLOSE_DELAY_MS);
	}, [closeAndUnlock]);
	const handleTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}

		lockedByClick.current = true;
		open('click');
	}, [closeAndUnlock, isOpen, open]);
	const handleContentMouseEnter = useCallback(() => {
		cancelScheduledClose();
	}, [cancelScheduledClose]);
	const handleContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) {
			closeAndUnlock();
		}
	}, [closeAndUnlock]);
	const handleFeedbackClick = useCallback(
		(value: InferredProjectFeedbackValue) => {
			// Pin the card like a trigger click: showing or dismissing the confirmation resizes it,
			// which can move it out from under the pointer and would otherwise hover-close it.
			lockedByClick.current = true;
			if (feedback === value) {
				return;
			}
			setFeedback(value);
			setIsFeedbackConfirmationVisible(true);
			onFeedback?.(value, projectId);
		},
		[feedback, onFeedback, projectId],
	);
	const handleFeedbackConfirmationDismiss = useCallback(() => {
		// Keep focus inside the card so removing the dismiss button does not blur-close it.
		(feedback === 'dislike' ? dislikeButtonRef : likeButtonRef).current?.focus();
		setIsFeedbackConfirmationVisible(false);
	}, [feedback]);
	const handleContentBlur = useCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				handleContentMouseLeave();
			}
		},
		[handleContentMouseLeave],
	);

	const context = state.status === 'success' ? state.data : undefined;
	const title = context?.title ?? canonicalProjectName ?? projectName;
	const owner = context?.owners[0];
	const suggestedContributors = dedupeContributors([
		...(context?.owners ?? []),
		...(context?.collaborators ?? []),
	]);
	const confirmedContributors = dedupeContributors([
		...(context?.owners.slice(1) ?? []),
		...(context?.collaborators ?? []),
	]);
	const contributors = hasPromotedProject ? confirmedContributors : suggestedContributors;
	const hasMoreContributors = contributors.length > COMPACT_CONTRIBUTOR_LIMIT;
	const visibleContributors = isShowingAllContributors
		? contributors
		: contributors.slice(0, COMPACT_CONTRIBUTOR_LIMIT);
	const source = context?.sources[0];
	// Checked before the gate so closed chips and consumers that do not record feedback (no
	// `onFeedback`, e.g. Lumina) do not log gate exposures.
	const isFeedbackFooterVisible =
		isOpen &&
		Boolean(onFeedback) &&
		fg('rovo_chat_inferred_project_card_feedback') &&
		Boolean(context);

	// Uses inline button semantics so long project names wrap within response text.
	return (
		<Popup
			isOpen={isOpen}
			onClose={closeAndUnlock}
			placement="bottom-start"
			content={() => (
				<Box
					onBlur={handleContentBlur}
					onFocus={handleContentMouseEnter}
					onMouseEnter={handleContentMouseEnter}
					onMouseLeave={handleContentMouseLeave}
					paddingBlockEnd={isFeedbackFooterVisible ? 'space.100' : 'space.200'}
					paddingBlockStart="space.150"
					paddingInline="space.200"
					xcss={styles.card}
				>
					<Stack space="space.100">
						<Stack space="space.025">
							<Heading color="color.text" size="xsmall">
								{title}
							</Heading>
							<Text color="color.text.subtlest" size="small">
								<TeamworkGraphIcon color={token('color.icon.subtlest')} label="" size="small" />{' '}
								{formatMessage(hasPromotedProject ? messages.project : messages.inferredProject)}
							</Text>
						</Stack>
						{state.status === 'loading' ? <Text>{formatMessage(messages.loading)}</Text> : null}
						{state.status === 'error' ? <Text>{formatMessage(messages.loadError)}</Text> : null}
						{context ? (
							<Stack space="space.100">
								{hasPromotedProject && source ? (
									<Stack space="space.100">
										<Button
											aria-expanded={isSourcesOpen}
											onClick={() => setIsSourcesOpen((isOpen) => !isOpen)}
											shouldFitContainer
										>
											{formatMessage(messages.sourcesAndRelatedLinks)}
										</Button>
										{isSourcesOpen ? (
											<Box xcss={styles.sourceLink}>
												{fg('rovo_chat_inferred_project_related_content') ? (
													// Merge the project's sources with fetched related content into one list. The
													// sources render immediately via the fallbacks, so they stay visible while
													// related content loads or if the query fails.
													<ErrorBoundary
														fallback={
															<SourcesLinkList
																sources={context.sources}
																onInteraction={onInteraction}
															/>
														}
													>
														<Suspense
															fallback={
																<SourcesLinkList
																	sources={context.sources}
																	onInteraction={onInteraction}
																/>
															}
														>
															<RelatedContentSection
																cloudId={cloudId}
																projectAri={projectId}
																primarySources={sourcesToRelatedLinks(context.sources)}
																showHeading={false}
																onInteraction={onInteraction}
															/>
														</Suspense>
													</ErrorBoundary>
												) : (
													<SourcesLinkList
														sources={context.sources}
														onInteraction={onInteraction}
													/>
												)}
											</Box>
										) : null}
									</Stack>
								) : source ? (
									<Box padding="space.100" xcss={styles.basedOn}>
										<Stack space="space.100">
											<Text color="color.text.subtlest" size="small">
												<span css={styles.flippedIcon}>
													<UndoIcon color={token('color.icon.subtlest')} label="" size="small" />
												</span>{' '}
												{formatMessage(messages.sourcedFrom)}
											</Text>
											<Box xcss={styles.sourceLink}>
												<LinkList
													openLinksInNewTab={isInferredProjectEntityAnnotationEnabled()}
													onLinkClick={(_url, _index, options) => {
														if (!options?.isContextMenu) {
															onInteraction?.('source_document');
														}
													}}
													expandedMaxSources={1}
													maxSources={1}
													showExpand={false}
													size="compact"
													titles={source.title ? [source.title] : undefined}
													urls={[source.url]}
												/>
											</Box>
										</Stack>
									</Box>
								) : null}
								{context.summary ? (
									<Text color="color.text.subtle" size="medium">
										{context.summary}
									</Text>
								) : null}
								{hasPromotedProject && owner ? (
									<Stack space="space.075">
										<Text color="color.text" size="small" weight="medium">
											{formatMessage(messages.owner)}
										</Text>
										<Inline shouldWrap space="space.075">
											<ContributorChip
												onClick={() => onInteraction?.('person_profile')}
												cloudId={cloudId}
												locale={locale}
												person={owner}
												profileClient={profileClient}
												renderProfileTrigger={renderProfileTrigger}
											/>
										</Inline>
									</Stack>
								) : null}
								<Stack space="space.150" xcss={styles.contributors}>
									<Inline alignBlock="center" spread="space-between">
										<Text color="color.text" size="small" weight="medium">
											{formatMessage(
												hasPromotedProject ? messages.contributors : messages.suggestedContributors,
											)}
										</Text>
										{hasMoreContributors && !isShowingAllContributors ? (
											<Pressable
												onClick={() => setIsShowingAllContributors(true)}
												xcss={styles.showAllTrigger}
											>
												<Text color="color.text.subtle" size="small">
													{formatMessage(messages.showAllContributors)}
												</Text>
											</Pressable>
										) : null}
									</Inline>
									{visibleContributors.length ? (
										<Inline shouldWrap space="space.075">
											{visibleContributors.map((contributor) => (
												<ContributorChip
													onClick={() => onInteraction?.('person_profile')}
													cloudId={cloudId}
													locale={locale}
													key={contributor.ari ?? contributor.name}
													person={contributor}
													profileClient={profileClient}
													renderProfileTrigger={renderProfileTrigger}
												/>
											))}
										</Inline>
									) : (
										<Text color="color.text.subtle">
											{formatMessage(
												hasPromotedProject
													? messages.noContributors
													: messages.noSuggestedContributors,
											)}
										</Text>
									)}
								</Stack>
								{!hasPromotedProject && fg('rovo_chat_inferred_project_related_content') ? (
									// Inferred (not yet promoted) projects show related content as its own section near the
									// bottom. Once promoted, related content is instead merged into the sources list above,
									// so this standalone section is only rendered while the project is still inferred.
									// Loads after the rest of the card: the query suspends independently, so related
									// content is appended once it resolves without blocking the sections above.
									<ErrorBoundary fallback={null}>
										<Suspense fallback={null}>
											<RelatedContentSection
												cloudId={cloudId}
												projectAri={projectId}
												// The sources are already shown higher up on the card for an inferred project, so
												// they are passed only to de-duplicate related content (renderPrimarySources=false)
												// and are not rendered again here.
												primarySources={sourcesToRelatedLinks(context.sources)}
												renderPrimarySources={false}
												onInteraction={onInteraction}
											/>
										</Suspense>
									</ErrorBoundary>
								) : null}
								<ErrorBoundary fallback={null}>
									<Suspense fallback={null}>
										<PromoteInferredProjectSection
											onInteraction={onInteraction}
											contributors={suggestedContributors}
											inferredProjectAri={projectId}
											promote={(ari) => {
												onInteraction?.('claim_project');
												promotion.promote(ari);
											}}
											state={promotion.state}
										/>
									</Suspense>
								</ErrorBoundary>
							</Stack>
						) : null}
						{isFeedbackFooterVisible ? (
							<Stack space="space.100">
								<Inline
									alignBlock="center"
									space="space.100"
									spread="space-between"
									xcss={styles.feedbackFooter}
								>
									<Inline alignBlock="center" space="space.050">
										<InformationCircleIcon
											color={token('color.icon.subtlest')}
											label=""
											size="small"
										/>
										<Text color="color.text.subtlest" size="small">
											{formatMessage(messages.aiDisclaimer)}
										</Text>
									</Inline>
									<Inline space="space.025">
										<IconButton
											ref={likeButtonRef}
											aria-pressed={feedback === 'like'}
											appearance="subtle"
											// Unselected thumbs match the subtlest disclaimer text; selected uses the selected icon colour.
											icon={(iconProps) => (
												<ThumbsUpIcon
													{...iconProps}
													color={
														feedback === 'like'
															? token('color.icon.selected')
															: token('color.icon.subtlest')
													}
													size="small"
												/>
											)}
											isSelected={feedback === 'like'}
											isTooltipDisabled={false}
											label={formatMessage(messages.goodSuggestion)}
											onClick={() => handleFeedbackClick('like')}
											spacing="compact"
											// Opens above so it never covers the confirmation below the footer.
											tooltip={{ position: 'top' }}
										/>
										<IconButton
											ref={dislikeButtonRef}
											aria-pressed={feedback === 'dislike'}
											appearance="subtle"
											icon={(iconProps) => (
												<ThumbsDownIcon
													{...iconProps}
													color={
														feedback === 'dislike'
															? token('color.icon.selected')
															: token('color.icon.subtlest')
													}
													size="small"
												/>
											)}
											isSelected={feedback === 'dislike'}
											isTooltipDisabled={false}
											label={formatMessage(messages.poorSuggestion)}
											onClick={() => handleFeedbackClick('dislike')}
											spacing="compact"
											tooltip={{ position: 'top' }}
										/>
									</Inline>
								</Inline>
								<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
									{isFeedbackConfirmationVisible ? formatMessage(messages.feedbackThankYou) : null}
								</VisuallyHidden>
								{isFeedbackConfirmationVisible ? (
									<Inline
										alignBlock="center"
										space="space.100"
										spread="space-between"
										testId="inferred-project-card-feedback-confirmation"
										xcss={styles.feedbackConfirmation}
									>
										<Inline alignBlock="center" space="space.100">
											<CheckCircleIcon color={token('color.icon.success')} label="" />
											<Text color="color.text.subtle">
												{formatMessage(messages.feedbackThankYou)}
											</Text>
										</Inline>
										<IconButton
											appearance="subtle"
											icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
											isTooltipDisabled={false}
											label={formatMessage(messages.dismissFeedbackConfirmation)}
											onClick={handleFeedbackConfirmationDismiss}
											spacing="compact"
										/>
									</Inline>
								) : null}
							</Stack>
						) : null}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button -- The project treatment must wrap with response text, which standard buttons cannot support.
				<span
					{...triggerProps}
					aria-label={formatMessage(
						hasPromotedProject ? messages.projectTrigger : messages.inferredProjectTrigger,
						{ projectName },
					)}
					css={projectTitleButtonStyles}
					onClick={handleTriggerClick}
					onKeyDown={(event) => {
						if (
							event.key === 'Enter' ||
							event.key === ' ' ||
							event.key === 'Space' ||
							event.key === 'Spacebar' ||
							event.code === 'Space'
						) {
							event.preventDefault();
							handleTriggerClick();
						}
					}}
					onBlur={handleTriggerMouseLeave}
					onFocus={handleTriggerMouseEnter}
					onMouseEnter={handleTriggerMouseEnter}
					onMouseLeave={handleTriggerMouseLeave}
					role="button"
					tabIndex={0}
				>
					<span>
						<span css={projectIconStyles}>
							<IconTile appearance="purple" icon={ProjectIcon} label="" size="xsmall" />
						</span>
						<span css={projectTitleStyles}>{projectName}</span>
					</span>
					<span css={projectLabelStyles}>
						<Lozenge appearance="discovery">
							<Inline space="space.050" alignBlock="center">
								<TeamworkGraphIcon color={token('color.icon.subtlest')} label="" size="small" />
								{formatMessage(hasPromotedProject ? messages.project : messages.inferredProject)}
							</Inline>
						</Lozenge>
					</span>
				</span>
			)}
		/>
	);
};

export const InferredProjectInlineCard = ({
	fallback,
	...props
}: InferredProjectInlineCardProps): React.ReactNode => (
	<ErrorBoundary fallback={<>{fallback}</>}>
		<AtlassianRelayEnvironmentProvider>
			<InferredProjectCardInner key={`${props.cloudId}:${props.projectId}`} {...props} />
		</AtlassianRelayEnvironmentProvider>
	</ErrorBoundary>
);
